import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Loader2, Download, Save, Plus, CheckCircle2, AlertTriangle, Play, Pause } from 'lucide-react';
import { getCurrentUser } from '../services/auth';

/**
 * WatermarkExportModal — overlay a brand logo onto a Media Library VIDEO and
 * download it and/or save a copy to the library. Live preview is drawn on a
 * canvas (poster frame + logo) so placement/size/corner are judged instantly;
 * the real video is rendered server-side (media-watermark + ffmpeg) on action.
 *
 * Props: item (video asset), onClose, onSaved (refresh the library).
 */
const CORNERS = [
  { id: 'tl', label: 'Top left' }, { id: 'tr', label: 'Top right' },
  { id: 'bl', label: 'Bottom left' }, { id: 'br', label: 'Bottom right' },
];

async function mw(action, params = {}) {
  const base = window.location.hostname === 'localhost' ? 'http://localhost:8888/.netlify/functions' : '/.netlify/functions';
  const res = await fetch(`${base}/media-watermark`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action, ...params }) });
  const d = await res.json().catch(() => ({}));
  if (!res.ok || d.error) throw new Error(d.error || `error ${res.status}`);
  return d;
}
function downscalePng(file, maxW) {
  return new Promise((resolve, reject) => {
    const img = new Image(); const url = URL.createObjectURL(file);
    img.onload = () => {
      const scale = Math.min(1, maxW / img.width);
      const w = Math.round(img.width * scale), h = Math.round(img.height * scale);
      const c = document.createElement('canvas'); c.width = w; c.height = h;
      c.getContext('2d').drawImage(img, 0, 0, w, h);
      URL.revokeObjectURL(url); resolve(c.toDataURL('image/png'));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('could not read image')); };
    img.src = url;
  });
}

export default function WatermarkExportModal({ item, onClose, onSaved }) {
  const user = getCurrentUser();
  const [logos, setLogos] = useState([]);
  const [logoId, setLogoId] = useState('default');
  const [corner, setCorner] = useState('br');
  const [widthPct, setWidthPct] = useState(0.18);
  const [shadow, setShadow] = useState(true);
  const [watermarkOn, setWatermarkOn] = useState(true);
  const [tracks, setTracks] = useState([]);
  const [audioTrackId, setAudioTrackId] = useState('');   // '' = no music
  const [audioVolume, setAudioVolume] = useState(1);
  const [audioMode, setAudioMode] = useState('replace');  // replace | mix
  const [busy, setBusy] = useState(null);     // 'download' | 'save'
  const [msg, setMsg] = useState(null);
  const logoFileRef = useRef(null);
  const audioFileRef = useRef(null);
  const audioRef = useRef(null);
  const [playingId, setPlayingId] = useState(null);

  const canvasRef = useRef(null);
  const posterRef = useRef(null);
  const logoImgs = useRef({});
  const [tick, setTick] = useState(0);
  const redraw = () => setTick((t) => t + 1);

  const loadLogos = useCallback(async () => {
    try { const r = await mw('logos'); setLogos(r.logos || []); if (r.logos && r.logos[0] && !r.logos.find((l) => l.id === logoId)) setLogoId(r.logos[0].id); }
    catch (e) { setMsg({ type: 'error', text: e.message }); }
  }, [logoId]);
  useEffect(() => { loadLogos(); /* eslint-disable-next-line */ }, []);

  const loadTracks = useCallback(async () => {
    try { const r = await mw('audio-list'); setTracks(r.tracks || []); } catch (e) { /* music is optional */ }
  }, []);
  useEffect(() => { loadTracks(); }, [loadTracks]);

  const onAddTrack = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (audioFileRef.current) audioFileRef.current.value = '';
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setMsg({ type: 'error', text: 'Audio file is too big — use a clip under 5 MB (it loops to fit the video).' }); return; }
    try {
      const dataUrl = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });
      const ext = (file.name.match(/\.([a-z0-9]+)$/i) || [])[1] || 'mp3';
      const r = await mw('audio-add', { name: file.name.replace(/\.[a-z0-9]+$/i, ''), audioBase64: dataUrl, ext, contentType: file.type || 'audio/mpeg' });
      await loadTracks(); if (r.id) setAudioTrackId(r.id);
    } catch (err) { setMsg({ type: 'error', text: 'Could not add track: ' + err.message }); }
  };

  const playPreview = (t) => {
    const a = audioRef.current; if (!a || !t.previewUrl) return;
    if (playingId === t.id) { a.pause(); setPlayingId(null); return; }
    a.src = t.previewUrl;
    a.play().then(() => setPlayingId(t.id)).catch(() => setPlayingId(null));
  };

  // poster frame
  useEffect(() => {
    const img = new Image();
    img.onload = () => { posterRef.current = img; redraw(); };
    img.src = `/.netlify/functions/r2-image?key=${encodeURIComponent(item.r2_thumb_key)}`;
  }, [item.r2_thumb_key]);

  // logo images
  useEffect(() => {
    logos.forEach((l) => {
      if (logoImgs.current[l.id]) return;
      const img = new Image();
      logoImgs.current[l.id] = { img, ready: false };
      img.onload = () => { logoImgs.current[l.id].ready = true; redraw(); };
      img.src = l.dataUrl;
    });
  }, [logos]);

  // draw preview
  useEffect(() => {
    const canvas = canvasRef.current, poster = posterRef.current;
    if (!canvas || !poster) return;
    const ar = poster.naturalWidth / poster.naturalHeight || 1;
    let h = Math.min(340, poster.naturalHeight || 340), w = Math.round(h * ar);
    if (w > 380) { w = 380; h = Math.round(w / ar); }
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, w, h); ctx.drawImage(poster, 0, 0, w, h);
    const le = logoImgs.current[logoId];
    if (watermarkOn && le && le.ready) {
      const lw = Math.round(w * widthPct);
      const lh = Math.round(lw * (le.img.naturalHeight / le.img.naturalWidth || 1));
      const pad = Math.round(w * 0.035);
      let x = w - lw - pad, y = h - lh - pad;
      if (corner === 'bl') { x = pad; }
      else if (corner === 'tr') { y = pad; }
      else if (corner === 'tl') { x = pad; y = pad; }
      ctx.save();
      if (shadow) { ctx.shadowColor = 'rgba(0,0,0,0.55)'; ctx.shadowBlur = Math.max(2, Math.round(lw * 0.06)); ctx.shadowOffsetX = Math.max(2, Math.round(lw * 0.04)); ctx.shadowOffsetY = Math.max(2, Math.round(lw * 0.04)); }
      ctx.drawImage(le.img, x, y, lw, lh);
      ctx.restore();
    }
  }, [tick, logoId, corner, widthPct, shadow, watermarkOn]);

  const pollJob = async (jobId) => {
    for (let i = 0; i < 150; i++) {
      await new Promise((r) => setTimeout(r, 2500));
      const r = await mw('job', { id: jobId });
      if (r.job && (r.job.status === 'done' || r.job.status === 'failed')) return r.job;
    }
    throw new Error('render timed out');
  };

  const run = async (save) => {
    if (busy) return;
    setBusy(save ? 'save' : 'download'); setMsg(null);
    try {
      const r = await mw('start-video', { assetId: item.id, logoId, corner, widthPct, shadow, watermark: watermarkOn, audioTrackId: audioTrackId || null, audioVolume, audioMode, save, createdByAdminId: user?.id });
      if (r.error) throw new Error(r.error);
      const job = await pollJob(r.jobId);
      if (job.status === 'failed') throw new Error(job.error || 'render failed');
      if (save) { setMsg({ type: 'ok', text: 'Saved a watermarked copy to the Media Library.' }); if (onSaved) onSaved(); }
      else {
        const fn = `${String(item.suggested_name || 'video').replace(/[^a-z0-9-_]+/gi, '-')}-watermarked.mp4`;
        const a = document.createElement('a');
        a.href = `/.netlify/functions/r2-fetch?key=${encodeURIComponent(job.out_key)}&filename=${encodeURIComponent(fn)}`;
        a.download = fn; document.body.appendChild(a); a.click(); a.remove();
        setMsg({ type: 'ok', text: 'Watermarked video downloaded.' });
      }
    } catch (e) { setMsg({ type: 'error', text: e.message }); }
    finally { setBusy(null); }
  };

  const onAddLogo = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (logoFileRef.current) logoFileRef.current.value = '';
    if (!file) return;
    try {
      const dataUrl = await downscalePng(file, 600);
      const r = await mw('logo-add', { name: file.name.replace(/\.[a-z0-9]+$/i, ''), logoBase64: dataUrl });
      await loadLogos(); if (r.id) setLogoId(r.id);
    } catch (err) { setMsg({ type: 'error', text: 'Could not add logo: ' + err.message }); }
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 10, width: '100%', maxWidth: 760, maxHeight: '92vh', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '1px solid #e5e5ea' }}>
          <h3 style={{ margin: 0, fontSize: 16, color: 'var(--navy)' }}>Export video — logo &amp; music</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}><X size={18} color="#6e6e73" /></button>
        </div>

        {msg ? (
          <div style={{ margin: '12px 18px 0', padding: '8px 12px', borderRadius: 7, fontSize: 12.5, display: 'flex', alignItems: 'center', gap: 7, background: msg.type === 'ok' ? '#e6f6ec' : '#fdecec', color: msg.type === 'ok' ? '#1b7a3d' : '#c0392b' }}>
            {msg.type === 'ok' ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />}{msg.text}
          </div>
        ) : null}

        <div style={{ display: 'flex', gap: 18, padding: 18, flexWrap: 'wrap' }}>
          {/* preview */}
          <div style={{ flex: '0 0 auto' }}>
            <div style={{ fontSize: 11, color: '#6e6e73', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Preview</div>
            <canvas ref={canvasRef} style={{ background: '#0e0e10', borderRadius: 8, maxWidth: '100%' }} />
            <div style={{ fontSize: 11, color: '#9b9b9f', marginTop: 6, maxWidth: 380 }}>Preview is a single frame; the saved/downloaded video has the logo on every frame.</div>
          </div>

          {/* controls */}
          <div style={{ flex: 1, minWidth: 240 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, fontWeight: 600, color: 'var(--navy)', cursor: 'pointer', marginBottom: 12 }}>
              <input type="checkbox" checked={watermarkOn} onChange={(e) => setWatermarkOn(e.target.checked)} /> Add logo watermark
            </label>
            {watermarkOn && (<>
            <div style={{ fontSize: 11, color: '#6e6e73', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Logo</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
              {logos.map((l) => (
                <button key={l.id} onClick={() => setLogoId(l.id)} title={l.name}
                  style={{ width: 54, height: 54, borderRadius: 8, padding: 4, cursor: 'pointer', background: '#0e0e10', border: logoId === l.id ? '2px solid var(--orange)' : '1px solid #e5e5ea' }}>
                  <img src={l.dataUrl} alt={l.name} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                </button>
              ))}
              <button onClick={() => logoFileRef.current && logoFileRef.current.click()} title="Add a logo"
                style={{ width: 54, height: 54, borderRadius: 8, cursor: 'pointer', background: '#f5f5f7', border: '1px dashed #c7c7cc', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6e6e73' }}>
                <Plus size={18} />
              </button>
              <input ref={logoFileRef} type="file" accept="image/png,image/*" onChange={onAddLogo} style={{ display: 'none' }} />
            </div>

            <div style={{ fontSize: 11, color: '#6e6e73', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Corner</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 14, maxWidth: 240 }}>
              {CORNERS.map((c) => (
                <button key={c.id} onClick={() => setCorner(c.id)}
                  style={{ padding: '7px 8px', fontSize: 12, borderRadius: 6, cursor: 'pointer', background: corner === c.id ? 'var(--navy)' : '#fff', color: corner === c.id ? '#fff' : 'var(--navy)', border: '1px solid ' + (corner === c.id ? 'var(--navy)' : '#e5e5ea') }}>
                  {c.label}
                </button>
              ))}
            </div>

            <div style={{ fontSize: 11, color: '#6e6e73', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Size — {Math.round(widthPct * 100)}% of width</div>
            <input type="range" min="8" max="40" value={Math.round(widthPct * 100)} onChange={(e) => setWidthPct(Number(e.target.value) / 100)} style={{ width: '100%', marginBottom: 12 }} />

            <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, color: 'var(--navy)', cursor: 'pointer' }}>
              <input type="checkbox" checked={shadow} onChange={(e) => setShadow(e.target.checked)} /> Drop shadow
            </label>
            </>)}

            {/* Music */}
            <div style={{ fontSize: 11, color: '#6e6e73', textTransform: 'uppercase', letterSpacing: 0.5, margin: '18px 0 6px' }}>Music</div>
            <div style={{ border: '1px solid #e5e5ea', borderRadius: 8, overflow: 'hidden', marginBottom: 8 }}>
              <div onClick={() => setAudioTrackId('')} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', cursor: 'pointer', fontSize: 13, color: 'var(--navy)', background: audioTrackId === '' ? '#fff5ef' : '#fff' }}>
                <span style={{ width: 22, flexShrink: 0 }} />No music
                {audioTrackId === '' ? <CheckCircle2 size={14} color="var(--orange)" style={{ marginLeft: 'auto' }} /> : null}
              </div>
              {tracks.map((t) => (
                <div key={t.id} onClick={() => setAudioTrackId(t.id)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', cursor: 'pointer', fontSize: 13, color: 'var(--navy)', background: audioTrackId === t.id ? '#fff5ef' : '#fff', borderTop: '1px solid #f0f0f2' }}>
                  <button onClick={(e) => { e.stopPropagation(); playPreview(t); }} title="Listen"
                    style={{ width: 22, height: 22, borderRadius: '50%', border: 'none', cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: playingId === t.id ? 'var(--navy)' : '#eef0f3', color: playingId === t.id ? '#fff' : 'var(--navy)' }}>
                    {playingId === t.id ? <Pause size={11} /> : <Play size={11} style={{ marginLeft: 1 }} />}
                  </button>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name}</span>
                  {audioTrackId === t.id ? <CheckCircle2 size={14} color="var(--orange)" style={{ marginLeft: 'auto', flexShrink: 0 }} /> : null}
                </div>
              ))}
            </div>
            <button onClick={() => audioFileRef.current && audioFileRef.current.click()} title="Add a music track (mp3 / m4a)"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 10px', borderRadius: 6, cursor: 'pointer', background: '#f5f5f7', border: '1px solid #e5e5ea', color: 'var(--navy)', fontSize: 12, fontWeight: 600, marginBottom: 8 }}>
              <Plus size={14} /> Add track
            </button>
            <input ref={audioFileRef} type="file" accept="audio/*,.mp3,.m4a,.wav" onChange={onAddTrack} style={{ display: 'none' }} />
            <audio ref={audioRef} onEnded={() => setPlayingId(null)} style={{ display: 'none' }} />
            {audioTrackId ? (
              <div>
                <div style={{ fontSize: 11, color: '#6e6e73', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Music volume — {Math.round(audioVolume * 100)}%</div>
                <input type="range" min="0" max="150" value={Math.round(audioVolume * 100)} onChange={(e) => setAudioVolume(Number(e.target.value) / 100)} style={{ width: '100%', marginBottom: 10 }} />
                <div style={{ display: 'flex', gap: 6 }}>
                  {[{ k: 'replace', label: 'Music only' }, { k: 'mix', label: 'Under original sound' }].map((m) => (
                    <button key={m.k} onClick={() => setAudioMode(m.k)}
                      style={{ flex: 1, padding: '7px 8px', fontSize: 12, borderRadius: 6, cursor: 'pointer', background: audioMode === m.k ? 'var(--navy)' : '#fff', color: audioMode === m.k ? '#fff' : 'var(--navy)', border: '1px solid ' + (audioMode === m.k ? 'var(--navy)' : '#e5e5ea') }}>
                      {m.label}
                    </button>
                  ))}
                </div>
                <div style={{ fontSize: 10.5, color: '#9b9b9f', marginTop: 6 }}>The track loops to fit and is trimmed to the video length.</div>
              </div>
            ) : null}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, padding: '14px 18px', borderTop: '1px solid #e5e5ea', flexWrap: 'wrap' }}>
          <button className="btn btn-primary" onClick={() => run(false)} disabled={!!busy || (!watermarkOn && !audioTrackId)} style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
            {busy === 'download' ? <><Loader2 size={14} className="spin" /> Rendering…</> : <><Download size={14} /> Export &amp; download</>}
          </button>
          <button className="btn btn-navy" onClick={() => run(true)} disabled={!!busy || (!watermarkOn && !audioTrackId)} style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
            {busy === 'save' ? <><Loader2 size={14} className="spin" /> Rendering…</> : <><Save size={14} /> Save to Media Library</>}
          </button>
          <a href={`/.netlify/functions/r2-fetch?key=${encodeURIComponent(item.r2_original_key)}&filename=${encodeURIComponent(item.original_name || 'video.mp4')}`} download
            className="btn btn-outline" style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            Download original (no logo)
          </a>
        </div>
      </div>
    </div>
  );
}
